import { useSkin } from './skin';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { Locale } from '@blauturquesa/shared/locales';
import type { PublicPropertyDoc } from '@blauturquesa/shared/public-view';
import { Icon } from '../../components/Icon';
import { Loading } from '../../components/Loading';
import { getPublicProperty } from './publicData';
import { CaptionTag, captionText, Photo, pick } from './bits';

/**
 * Slider de fotos (§10.2): les fotos «Al slider», a pantalla completa, sense entrar a la fitxa.
 * Lliscant al mòbil (scroll-snap), amb fletxes i teclat a l'escriptori. Fora de la carcassa.
 */
export function PhotoSliderPage() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('public');
  const locale = i18n.language as Locale;
  const [view, setView] = useState<PublicPropertyDoc | null | undefined>(undefined);
  useSkin(view?.skin?.tokens);
  const [index, setIndex] = useState(0);
  const track = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getPublicProperty(slug).then(setView, () => setView(null));
  }, [slug]);

  const photos = view?.photos ?? [];
  const go = useCallback((i: number) => {
    const el = track.current;
    if (!el) return;
    const next = Math.max(0, Math.min(photos.length - 1, i));
    el.scrollTo({ left: next * el.clientWidth, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }, [photos.length]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') go(index + 1);
      else if (e.key === 'ArrowLeft') go(index - 1);
      else if (e.key === 'Escape') navigate(`/p/${slug}`);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [go, index, navigate, slug]);

  const back = `/p/${slug}`;
  if (view === undefined) return <div className="slider"><Loading /></div>;
  if (view === null || photos.length === 0) {
    return (
      <div className="slider slider-empty">
        <p>{t(view === null ? 'notFound.title' : 'noPhotos')}</p>
        <Link className="btn" to={view === null ? '/explora' : back}>{t('backToProperty')}</Link>
      </div>
    );
  }

  const src = view.sourceLocale;
  const current = photos[index];
  const caption = current ? pick(current.alt, locale, src).text : '';
  return (
    <div className="slider" role="region" aria-roledescription="carousel" aria-label={pick(view.title, locale, src).text}>
      <div className="slider-top">
        <Link className="icon-btn glass" to={back} aria-label={t('backToProperty')}><Icon name="arrowLeft" size={20} /></Link>
        <span className="slider-counter glass" aria-live="polite">{t('counter', { n: index + 1, total: photos.length })}</span>
      </div>
      <div
        ref={track}
        className="slider-track"
        onScroll={(e) => {
          const el = e.currentTarget;
          setIndex(Math.round(el.scrollLeft / Math.max(1, el.clientWidth)));
        }}
      >
        {photos.map((p, i) => (
          <figure key={p.id} className="slider-slide" aria-roledescription="slide" aria-label={t('counter', { n: i + 1, total: photos.length })}>
            <Photo photo={p} sizes="100vw" locale={locale} source={src} eager={i === 0} />
            <CaptionTag text={captionText(p.caption, { ...(view.priceFrom ? { price: view.priceFrom } : {}), modes: view.modes, locale, source: src, t })} className="slider-tag" />
          </figure>
        ))}
      </div>
      {caption && <p className="slider-caption glass">{caption}</p>}
      <button type="button" className="slider-arrow prev icon-btn glass" aria-label={t('prev')} disabled={index === 0} onClick={() => go(index - 1)}><Icon name="chevronLeft" size={22} /></button>
      <button type="button" className="slider-arrow next icon-btn glass" aria-label={t('next')} disabled={index >= photos.length - 1} onClick={() => go(index + 1)}><Icon name="chevronRight" size={22} /></button>
      <div className="slider-actions">
        <Link className="btn glass grow" to={back}>{t('seeProperty')}</Link>
        <Link className="btn-shiny grow" to={`/p/${slug}/consulta`}>{t('checkDates')}</Link>
      </div>
    </div>
  );
}
